/**
 * Client for zeecore `/briefs`. Reading needs only a signed-in user of the
 * organisation; changing or running a brief needs the `zoe-briefs` permission,
 * enforced server-side — the page only decides which controls to show.
 */
import { API_ENDPOINTS } from '../config/api';
import { authenticatedFetch } from '../utils/apiClient';
import type {
  AlertState, BriefDefinition, BriefInput, BriefProposal, BriefRecipients, BriefRun, BriefRunSummary, BriefSubscriptions, NotifyChannel,
} from '../types/briefs';

const BRIEFS = `${API_ENDPOINTS.base || ''}/briefs`;
/** Preview and runs read the source; allow for a cold Athena query. */
const RUN_TIMEOUT_MS = 120_000;

/** Carries the HTTP status so the page can tell "no access" and "stale edit" from a failure. */
export class BriefsApiError extends Error {
  constructor(message: string, readonly status: number, readonly body: Record<string, unknown> | null) {
    super(message);
    this.name = 'BriefsApiError';
  }
}

async function send<T>(path: string, init: RequestInit = {}, timeoutMs?: number): Promise<T> {
  const res = await authenticatedFetch(`${BRIEFS}${path}`, init, timeoutMs);
  if (res.status === 204) return undefined as T;
  const body = (await res.json().catch(() => null)) as Record<string, unknown> | null;
  if (!res.ok) {
    const problems = Array.isArray(body?.problems) ? `: ${(body!.problems as string[]).join('; ')}` : '';
    throw new BriefsApiError(`${(body?.error as string) || `Request failed (${res.status})`}${problems}`, res.status, body);
  }
  return body as T;
}

const id = encodeURIComponent;

/** A list the server should always send; anything else is an error to show, never a crash. */
function asList<T>(value: unknown, what: string): T[] {
  if (!Array.isArray(value)) throw new Error(`Unexpected response from the briefs service (no ${what} list)`);
  return value as T[];
}

export const briefsApi = {
  list: () => send<{ briefs: unknown }>('').then((r) => asList<BriefDefinition>(r?.briefs, 'briefs')),
  /** One brief; an alert also says what it last knew (null before its first scheduled run). */
  get: (briefId: string) =>
    send<{ brief: BriefDefinition; alert_state?: AlertState | null; data_triggers?: string[] }>(`/${id(briefId)}`)
      .then((r) => ({ brief: r.brief, alertState: r.alert_state ?? null, dataTriggers: r.data_triggers ?? [] })),
  update: (briefId: string, revision: number, input: BriefInput) =>
    send<{ brief: BriefDefinition }>(`/${id(briefId)}`, {
      method: 'PUT', body: JSON.stringify({ ...input, revision }),
    }).then((r) => r.brief),
  remove: (briefId: string) => send<void>(`/${id(briefId)}`, { method: 'DELETE' }),
  /** Anyone who can read a brief may subscribe themselves (R6-4). 404: notifications are not switched on. */
  subscriptions: (briefId: string) => send<BriefSubscriptions>(`/${id(briefId)}/subscriptions`),
  setMySubscription: (briefId: string, channels: NotifyChannel[]) =>
    send<{ mine: NotifyChannel[] }>(`/${id(briefId)}/subscriptions/me`, { method: 'PUT', body: JSON.stringify({ channels }) }).then((r) => r.mine),
  /** Addresses the brief is also emailed to, e.g. a group mailbox. Needs zoe-briefs. */
  recipients: (briefId: string) => send<BriefRecipients>(`/${id(briefId)}/recipients`),
  setRecipients: (briefId: string, addresses: string[]) =>
    send<BriefRecipients>(`/${id(briefId)}/recipients`, { method: 'PUT', body: JSON.stringify({ addresses }) }),
  preview: (briefId: string) =>
    send<{ run: BriefRun }>(`/${id(briefId)}/preview`, { method: 'POST' }, RUN_TIMEOUT_MS).then((r) => r.run),
  run: (briefId: string) =>
    send<{ run: BriefRun }>(`/${id(briefId)}/runs`, { method: 'POST' }, RUN_TIMEOUT_MS).then((r) => r.run),
  runs: (briefId: string) => send<{ runs: unknown }>(`/${id(briefId)}/runs`).then((r) => asList<BriefRunSummary>(r?.runs, 'runs')),
  getRun: (briefId: string, runId: string) =>
    send<{ run: BriefRun }>(`/${id(briefId)}/runs/${id(runId)}`).then((r) => r.run),
  /** The user's Zoe drafts that were never created or cancelled, newest first. */
  listDrafts: () => send<{ drafts: unknown }>('/drafts').then((r) => asList<BriefProposal>(r?.drafts, 'drafts')),
  /** Zoe drafts: the card names the revision it showed, so a stale card is refused (409), never a different brief. */
  getDraft: (conversationId: string) =>
    send<{ draft: BriefProposal }>(`/drafts/${id(conversationId)}`).then((r) => r.draft),
  confirmDraft: (p: Pick<BriefProposal, 'conversation_id' | 'draft_id' | 'revision'>) =>
    send<{ brief: BriefDefinition }>(`/drafts/${id(p.conversation_id)}/confirm`, {
      method: 'POST', body: JSON.stringify({ draft_id: p.draft_id, revision: p.revision }),
    }).then((r) => r.brief),
  cancelDraft: (p: Pick<BriefProposal, 'conversation_id' | 'draft_id' | 'revision'>) =>
    send<{ draft: BriefProposal }>(`/drafts/${id(p.conversation_id)}/cancel`, {
      method: 'POST', body: JSON.stringify({ draft_id: p.draft_id, revision: p.revision }),
    }).then((r) => r.draft),
};
